import React from 'react';
import classnames from 'classnames';

import { Drawer, Backdrop } from '@material-ui/core';
import { makeStyles, createStyles } from '@material-ui/core/styles';

import RailNavigationListItem, { ItemProps } from '@hydra/common/components/RailNavigation/RailNavigationListItem';

import { useI18n } from '@hydra/common/customHooks';

export const useStyles = makeStyles((theme) => createStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    color: theme.palette.background.secondary
  },
  drawer: {
    height: `calc(100vh - ${theme.mixins.toolbar.minHeight}px)`,
    width: 60,
    marginTop: theme.mixins.toolbar.minHeight,
    zIndex: theme.zIndex.appBar - 1,
    backgroundColor: theme.palette.grey[900]
  },
  backdrop: {
    zIndex: theme.zIndex.appBar - 2,
    marginTop: theme.mixins.toolbar.minHeight,
    backgroundColor: 'rgba(0, 0, 0, 0.45)',
    [theme.breakpoints.up('md')]: {
      display: 'none'
    }
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    margin: 0,
    marginTop: theme.spacing(0.5), // Balance TopNavigation shadow
    padding: 0,
    height: '100%',
    width: '100%'
  },
  desktopDrawer: {
    display: 'none',
    [theme.breakpoints.up('md')]: {
      display: 'flex'
    }
  },
  mobileDrawer: {
    display: 'flex',
    [theme.breakpoints.up('md')]: {
      display: 'none'
    }
  }
}));

function getListItemsObject (active: string, locale: string): Array<ItemProps> {
  const overviewListItem: ItemProps = {
    id: 'overview',
    label: 'navigation_home',
    href: `/${locale}/overview`,
    icon: 'dashboard-home',
    active: (active === 'home' || active === 'overview'),
    className: 'nr-project-appnav-overview'
  };

  const auditListItem: ItemProps = {
    id: 'audit',
    label: 'navigation_audit',
    href: `/${locale}/audit`,
    active: (active === 'audit'),
    icon: 'audit',
    className: 'nr-project-appnav-audit'
  };

  const optimizeRoutes = [
    'optimize',
    'pages',
    'traffic',
    'marketing-checklist',
    'serp',
    'crawler',
    'assistant',
    'performance',
    'settings',
    'dashboard'
  ];

  const optimizeListItem: ItemProps = {
    id: 'optimize',
    label: 'navigation_optimize',
    href: `/${locale}/optimize`,
    active: (optimizeRoutes.includes(active)),
    icon: 'optimize-app',
    className: 'nr-project-appnav-optimize'
  };

  const pdfListItem: ItemProps = {
    id: 'pdf',
    label: 'navigation_pdf_templates',
    href: `/${locale}/pdf-editor`,
    active: (active === 'pdf-templates'),
    icon: 'pdf-templates',
    className: 'nr-project-appnav-pdf-templates'
  };

  const leadsListItem: ItemProps = {
    id: 'leads',
    label: 'navigation_leadgen_tool',
    href: `/${locale}/leads`,
    active: (active === 'leads'),
    icon: 'magnet',
    className: 'nr-project-appnav-leads'
  };

  return [
    overviewListItem,
    optimizeListItem,
    auditListItem,
    pdfListItem,
    leadsListItem
  ];
}

export interface MobileDrawerProps {
  open: boolean,
  onClose: any
  children?: React.ReactNode
}

const MobileDrawer: React.FC<MobileDrawerProps> = ({ open, onClose, children }) => {
  const classes = useStyles();
  return (
    <>
      <Backdrop
        open={open}
        onClick={onClose}
        className={classes.backdrop}
      />
      <Drawer
        PaperProps={{
          classes: {
            root: classes.drawer
          },
          elevation: 4
        }}
        variant='persistent'
        className={classes.mobileDrawer}
        open={open}
        onClose={onClose}
      >
        {children}
      </Drawer>
    </>
  );
};

export interface DesktopDrawerProps {
  children?: React.ReactNode
}

const DesktopDrawer: React.FC<DesktopDrawerProps> = ({ children }) => {
  const classes = useStyles();
  return (
    <Drawer
      PaperProps={{
        classes: {
          root: classes.drawer
        },
        elevation: 4
      }}
      className={classnames(classes.desktopDrawer)}
      open
      variant='permanent'
    >
      {children}
    </Drawer>
  );
};

export interface RailNavigationProps {
  active: string
  onClose: any
  open: boolean
}

export function RailNavigation ({ active = '', onClose = () => {}, open = false }: RailNavigationProps) {
  const { locale } = useI18n();
  const classes = useStyles();
  const navigationItems = getListItemsObject(active, locale);

  return (
    <div className={classnames(classes.root)} data-testid='tid-rail-navigation'>
      <DesktopDrawer>
        <div className={classnames(classes.list)}>
          {navigationItems.map((link, key) => <RailNavigationListItem item={link} key={key} />)}
        </div>
      </DesktopDrawer>
      <MobileDrawer open={open} onClose={onClose}>
        <div className={classnames(classes.list)}>
          {navigationItems.map((link, key) => <RailNavigationListItem item={link} key={key} />)}
        </div>
      </MobileDrawer>
    </div>
  );
}

export default React.memo(RailNavigation);
